// 育成の各要素（ツリー・装備の銘・お守り・式神）が出す効果の共通表現。loadout.ts で合算して BattleLoadout にする。

export type ModKey =
  // 基礎能力の%補正
  | 'hp'
  | 'atk'
  | 'def'
  | 'agi'
  // 固定値の加算
  | 'agiFlat'
  | 'move'
  | 'range'
  // 術
  | 'skillPower'
  | 'cd'
  | 'splash'
  // 与ダメ・被ダメ
  | 'core'
  | 'boss'
  | 'neutral'
  | 'taken'
  | 'lifesteal'
  // 薬
  | 'potionCount'
  | 'potionHeal'
  // 普請
  | 'unlock'
  | 'maxCount'
  | 'perTurn'
  | 'buildCost'
  | 'buildCostFlat'
  | 'buildHp'
  | 'buildPower'
  | 'buildRange'
  | 'matStart'
  | 'matIncome'
  // ルール
  | 'diceMin'
  | 'respawn'
  | 'territory'
  // 賽3つ（rules 2）：賽の合計・役で効く効果
  | 'diceEven'
  | 'diceOdd'
  | 'diceHigh'
  | 'diceLow'
  | 'dicePair'
  | 'diceRole'
  | 'diceRoleMat'
  | 'diceSkill'
  | 'diceLowAtk'
  | 'diceNoRole';

export interface Effect {
  key: ModKey;
  value: number;
}

export type Mods = Partial<Record<ModKey, number>>;

/** 相性1.5倍の対象外（段階的な効果） */
export const DISCRETE_KEYS: ReadonlySet<ModKey> = new Set<ModKey>([
  'unlock',
  'cd',
  'perTurn',
  'maxCount',
  'buildRange',
  'move',
  'range',
  'diceMin',
  'diceLow',
  'diceRoleMat',
  'respawn',
  'potionCount',
  'buildCostFlat',
]);

/** 能力値の%（HP・攻撃・防御・速さ）は、出どころごとの値も残す（能力値は整数なので、1つずつ切り上げて必ず効かせる。loadout の statWith） */
export const STAT_PARTS = Symbol('statParts');
const STAT_KEYS: ReadonlySet<ModKey> = new Set<ModKey>(['hp', 'atk', 'def', 'agi']);
export const statParts = (m: Mods, key: ModKey): number[] => (m as { [STAT_PARTS]?: Partial<Record<ModKey, number[]>> })[STAT_PARTS]?.[key] ?? [];

export function addMod(m: Mods, key: ModKey, value: number) {
  m[key] = (m[key] ?? 0) + value;
  if (STAT_KEYS.has(key) && value) {
    const t = m as { [STAT_PARTS]?: Partial<Record<ModKey, number[]>> };
    ((t[STAT_PARTS] ??= {})[key] ??= []).push(value);
  }
}

export const MOD_LABEL: Record<ModKey, string> = {
  hp: 'HP',
  atk: '攻撃',
  def: '防御',
  agi: '速さ',
  agiFlat: '速さ',
  move: '移動',
  range: '射程',
  skillPower: '術の威力',
  cd: '術の待ち手番',
  splash: '術の範囲',
  core: '本陣への与ダメ',
  boss: 'ボスへの与ダメ',
  neutral: '中立への与ダメ',
  taken: '被ダメ',
  lifesteal: '吸収',
  potionCount: '回復薬の数',
  potionHeal: '回復薬の回復',
  unlock: '建物の解放',
  maxCount: '建物の上限',
  perTurn: '1手番の建築回数',
  buildCost: '建築費',
  buildCostFlat: '建物の陣材',
  buildHp: '建物HP',
  buildPower: '建物の効果',
  buildRange: '建築の射程',
  matStart: '初期陣材',
  matIncome: '陣材収入',
  diceMin: '移動の最低歩数',
  respawn: '復活の手番',
  territory: '陣地率',
  diceEven: '丁（賽の合計が偶数）の手番の与ダメ',
  diceOdd: '半（賽の合計が奇数）の手番から被ダメの軽減',
  diceHigh: '大目（賽の合計が10以上）の手番の与ダメ',
  diceLow: '小目（賽の合計が9以下）の手番の移動',
  dicePair: '揃い目（同じ目がある）の手番の与ダメ',
  diceRole: '役が出た手番の与ダメ',
  diceRoleMat: '役が出たときの陣材',
  diceSkill: '術の賽の上がり幅',
  diceLowAtk: '賽の合計が9以下の手番の与ダメ',
  diceNoRole: '役が出なかった手番の与ダメ',
};
